import React from "react";
import { useSearchParams } from "react-router-dom";
import moment from "moment";
import {
  CreateJobInput,
  JobStatus,
  useCreateJobMutation,
  useUpdateJobMutation,
} from "../graphql";
import { useForm } from "@mantine/form";
import {
  Button,
  Checkbox,
  Input,
  SegmentedControl,
  Select,
  SimpleGrid,
  Text,
  TextInput,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import EmployeeInput from "./EmployeeInput";
import CustomerInput from "./CustomerInput";
import { showSuccessNotification } from "../utils/showNotification";
import { useTrackForm } from "../hooks/useTrackForm";
import {
  DEFAULT_RACKET_TYPE,
  RACKET_TYPE_OPTIONS,
  isRacketType,
  racketTypeFromParam,
} from "../utils/racketType";

interface Props {
  onSubmit: (jobId: string, customerId?: string) => void;
  job?: CreateJobInput & { id: string };
  customerId?: string;
  cols?: number;
  onSelectedCustomerId?: (id: string | undefined) => void;
}

const JOB_STATUS: { value: JobStatus; label: string }[] = [
  { value: "TODO", label: "To Do" },
  { value: "FINISHED", label: "Done" },
];

const JobForm: React.FC<Props> = ({
  onSubmit,
  job,
  customerId,
  cols,
  onSelectedCustomerId,
}) => {
  const [search] = useSearchParams();
  const { markDirty, trackSubmit, trackInvalid, trackFailure } =
    useTrackForm("job");
  const [createJob, createJobResponse] = useCreateJobMutation({
    refetchQueries: ["ListJobs"],
    onCompleted: () => {
      showSuccessNotification("Job created successfully");
    },
  });
  const [updateJob, updateJobResponse] = useUpdateJobMutation({
    refetchQueries: ["ListJobs"],
    onCompleted: () => {
      showSuccessNotification("Job updated successfully");
    },
  });
  const form = useForm<CreateJobInput>({
    initialValues: {
      customerId: job?.customerId || search.get("customerId") || customerId,
      name: job?.name || search.get("name") || undefined,
      racket: job?.racket || search.get("racket") || undefined,
      racketType:
        job?.racketType ??
        racketTypeFromParam(search.get("racketType")) ??
        DEFAULT_RACKET_TYPE,
      stringName: job?.stringName || search.get("stringName") || undefined,
      tension: job?.tension || search.get("tension") || undefined,
      gauge: job?.gauge || search.get("gauge") || undefined,
      status: job?.status || "TODO",
      receivedAt: job?.receivedAt || new Date().toISOString(),
      finishedAt: job?.finishedAt,
      paidAt: job?.paidAt || undefined,
      finishedById: job?.finishedById,
    },
    onValuesChange: (newValues, oldValues) => {
      markDirty();
      if (newValues.customerId !== oldValues.customerId) {
        onSelectedCustomerId?.(newValues.customerId || undefined);
      }
    },
  });

  const handleSubmit = async (values: CreateJobInput) => {
    if (job) {
      const result = await updateJob({
        variables: { input: { ...values, id: job.id } },
      }).catch((error) => {
        trackFailure({
          has_customer: Boolean(values.customerId),
          is_update: true,
        });
        throw error;
      });
      if (result.data?.updateJob) {
        trackSubmit({
          has_customer: Boolean(values.customerId),
          is_update: true,
        });
        onSubmit(job.id, values.customerId || undefined);
      }
    } else {
      const result = await createJob({
        variables: { input: values },
      }).catch((error) => {
        trackFailure({
          has_customer: Boolean(values.customerId),
          is_update: false,
        });
        throw error;
      });
      if (result.data?.createJob) {
        trackSubmit({
          has_customer: Boolean(values.customerId),
          is_update: false,
        });
        onSubmit(result.data.createJob.id, values.customerId || undefined);
      }
    }
  };

  const errorMessage =
    createJobResponse.error?.message || updateJobResponse.error?.message;

  return (
    <div>
      <form onSubmit={form.onSubmit(handleSubmit, trackInvalid)}>
        <SimpleGrid cols={{ sm: 1, md: cols || 2 }}>
          <CustomerInput
            id="job-customerId-input"
            label="Customer ID"
            {...form.getInputProps("customerId")}
          />
          <TextInput
            id="job-name-input"
            label="Name"
            {...form.getInputProps("name")}
          />
          <TextInput
            id="job-racket-input"
            label="Racket"
            {...form.getInputProps("racket")}
          />
          <Input.Wrapper label="Racket Type" labelElement="div">
            <SegmentedControl
              id="job-racketType-input"
              aria-label="Racket Type"
              fullWidth
              data={RACKET_TYPE_OPTIONS}
              value={form.values.racketType ?? DEFAULT_RACKET_TYPE}
              onChange={(value) => {
                if (isRacketType(value)) {
                  form.setFieldValue("racketType", value);
                }
              }}
            />
          </Input.Wrapper>
          <TextInput
            id="job-stringName-input"
            label="String"
            {...form.getInputProps("stringName")}
          />
          <TextInput
            id="job-gauge-input"
            label="Gauge"
            {...form.getInputProps("gauge")}
          />
          <TextInput
            id="job-tension-input"
            label="Tension"
            {...form.getInputProps("tension")}
          />
          <Select
            id="job-status-select"
            label="Status"
            clearable={false}
            {...form.getInputProps("status")}
            data={JOB_STATUS}
          />
          <Checkbox
            id="job-paid-checkbox"
            label="Paid"
            mt="md"
            checked={!!form.values.paidAt}
            onChange={(e) =>
              form.setFieldValue(
                "paidAt",
                e.currentTarget.checked
                  ? // Keep the original paid date when re-checking an already paid job.
                    job?.paidAt || new Date().toISOString()
                  : null
              )
            }
          />
          <DateInput
            id="job-receivedAt-input"
            label="Received At"
            allowDeselect
            {...form.getInputProps("receivedAt")}
            value={
              form.getInputProps("receivedAt").value
                ? moment(form.getInputProps("receivedAt").value).toDate()
                : null
            }
            clearable
          />
          <DateInput
            id="job-finishedAt-input"
            label="Finished At"
            allowDeselect
            {...form.getInputProps("finishedAt")}
            value={
              form.getInputProps("finishedAt").value
                ? moment(form.getInputProps("finishedAt").value).toDate() ||
                  new Date()
                : null
            }
            clearable
          />
          <EmployeeInput
            id="job-finishedById-input"
            label="Finished By ID"
            {...form.getInputProps("finishedById")}
          />
        </SimpleGrid>
        {errorMessage && (
          <Text mt="md" c="red">
            {errorMessage}
          </Text>
        )}
        <Button id="btn-save-job" fullWidth mt="md" type="submit">
          Save
        </Button>
      </form>
    </div>
  );
};

export default JobForm;
